import { Input } from "@/components/ui/input";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import {
  fill as fillPDFProvincialGr912,
  SCHEMA_TO_PDF_FIELD,
  PAGE_INDEX_FOR_SCHEMA_FIELD,
  STEP_ANCHOR_FIELDS,
} from "./fill.js";
import templateUrl from "./template.pdf";
import { PRESETS } from "./presets.js";
import { formSchema, defaultValues } from "./schema.js";
import { skillOptions } from "./options.js";
import { STEPS, STEP_FIELDS, TOP_STEPS, COURSE_SUB_STEPS, CREDIT_ROWS } from "./steps.js";
import { useReportForm } from "@/reports/shared_utils/useReportForm";
import { PillSelector } from "@/reports/report_components/PillSelector";
import { SectionHeading } from "@/reports/report_components/SectionHeading";
import { AICommentButton } from "@/reports/report_components/AICommentButton";
import { COURSE_CONFIG } from "./aiConfig";
import { FormFieldGrid } from "@/reports/report_components/FormFieldGrid";
import { StepIndicator } from "@/reports/report_components/StepIndicator";
import { ReportFormShell } from "@/reports/report_components/ReportFormShell";

// ─── Reusable primitives ──────────────────────────────────────────────────────

const SKILL_ROWS = [
  { key: 'Responsibility',  label: 'Responsibility'   },
  { key: 'Organization',    label: 'Organization'     },
  { key: 'IndependentWork', label: 'Independent Work' },
  { key: 'Collaboration',   label: 'Collaboration'    },
  { key: 'Initiative',      label: 'Initiative'       },
  { key: 'SelfRegulation',  label: 'Self-Regulation'  },
];

function TermColumn({ label, prefix, termKey, control }) {
  return (
    <div className="flex flex-col gap-3">
      <p className="text-[10px] font-bold uppercase tracking-widest text-center text-muted-foreground">{label}</p>
      <div className="flex items-end gap-2">
        <FormField control={control} name={`${prefix}${termKey}PercentageMark`} render={({ field }) => (
          <FormItem className="flex flex-col items-center">
            <span className="text-[8px] text-muted-foreground uppercase tracking-wide">% Mark</span>
            <FormControl><input {...field} maxLength={3} inputMode="numeric" className="w-14 h-10 text-center text-base font-bold border-2 border-border rounded-xl bg-input focus:border-primary focus:outline-none transition-colors" /></FormControl>
          </FormItem>
        )} />
        <FormField control={control} name={`${prefix}${termKey}Median`} render={({ field }) => (
          <FormItem className="flex flex-col items-center">
            <span className="text-[8px] text-muted-foreground uppercase tracking-wide">Median</span>
            <FormControl><input {...field} maxLength={3} inputMode="numeric" className="w-14 h-9 text-center text-sm font-semibold border border-border rounded-lg bg-input focus:border-primary focus:outline-none transition-colors" /></FormControl>
          </FormItem>
        )} />
        <FormField control={control} name={`${prefix}${termKey}CreditEarned`} render={({ field }) => (
          <FormItem className="flex flex-col items-center">
            <span className="text-[8px] text-muted-foreground uppercase tracking-wide">Credit</span>
            <FormControl><input {...field} maxLength={3} inputMode="decimal" className="w-10 h-9 text-center text-sm font-semibold border border-border rounded-lg bg-input focus:border-primary focus:outline-none transition-colors" /></FormControl>
          </FormItem>
        )} />
      </div>
      <div className="space-y-1">
        {SKILL_ROWS.map(({ key, label: skillLabel }) => (
          <div key={key} className="flex items-center justify-between gap-2">
            <span className="text-[10px] text-muted-foreground w-24 shrink-0">{skillLabel}</span>
            <FormField control={control} name={`${prefix}${termKey}${key}`} render={({ field }) => (
              <FormItem><FormControl><PillSelector size="sm" value={field.value} onChange={field.onChange} options={skillOptions} /></FormControl></FormItem>
            )} />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-1.5">
        {[
          { fieldKey: `${prefix}${termKey}ClassesMissed`, label: 'Missed' },
          { fieldKey: `${prefix}${termKey}TotalClasses`,  label: 'Total'  },
          { fieldKey: `${prefix}${termKey}TimesLate`,     label: 'Late'   },
        ].map(({ fieldKey, label: attLabel }) => (
          <FormField key={fieldKey} control={control} name={fieldKey} render={({ field }) => (
            <FormItem className="flex flex-col items-center">
              <span className="text-[8px] text-muted-foreground uppercase tracking-wide">{attLabel}</span>
              <FormControl><input {...field} maxLength={4} inputMode="numeric" className="w-full h-8 text-center text-sm border border-border rounded-lg bg-input focus:border-primary focus:outline-none transition-colors" /></FormControl>
            </FormItem>
          )} />
        ))}
      </div>
    </div>
  );
}

function CourseCard({ courseNum, semLabel, form, reportId }) {
  const n = courseNum;
  const prefix = `course${n}`;
  const { control } = form;
  return (
    <div className="rounded-2xl border border-border bg-card shadow-sm overflow-hidden">
      <div className="px-5 py-3 bg-muted/50 border-b border-border space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-xs font-bold text-muted-foreground uppercase tracking-widest">{semLabel} · Course {n}</span>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {[
            { name: `${prefix}Title`,   placeholder: 'Course Title'       },
            { name: `${prefix}Code`,    placeholder: 'Code (e.g. ENG3U)'  },
            { name: `${prefix}Teacher`, placeholder: 'Teacher Name'        },
          ].map(({ name, placeholder }) => (
            <FormField key={name} control={control} name={name} render={({ field }) => (
              <FormItem><FormControl><Input {...field} placeholder={placeholder} className="text-sm" /></FormControl></FormItem>
            )} />
          ))}
        </div>
        <div>
          <p className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground mb-1.5">Program Flags</p>
          <div className="flex gap-2">
            {['ESLELD', 'IEP', 'French', 'SHSM'].map((flag) => {
              const fieldName = `${prefix}${flag}`;
              return (
                <FormField key={fieldName} control={control} name={fieldName} render={({ field }) => (
                  <button type="button" onClick={() => field.onChange(field.value === 'yes' ? '' : 'yes')}
                    className={`px-3 py-1.5 rounded-lg border-2 text-xs font-bold transition-colors cursor-pointer ${
                      field.value === 'yes' ? 'bg-primary border-primary text-white' : 'bg-input border-border text-muted-foreground hover:bg-accent'
                    }`}
                  >{flag === 'ESLELD' ? 'ESL/ELD' : flag}</button>
                )} />
              );
            })}
          </div>
        </div>
      </div>
      <div className="p-5">
        <div className="grid grid-cols-2 gap-0">
          <div className="pr-4 border-r border-border">
            <TermColumn label="First Term" prefix={prefix} termKey="First" control={control} />
          </div>
          <div className="pl-4">
            <TermColumn label="Final Mark" prefix={prefix} termKey="Final" control={control} />
          </div>
        </div>
        <div className="mt-5 pt-4 border-t border-border">
          <AICommentButton
            config={COURSE_CONFIG(n)}
            form={form}
            fieldName={`${prefix}Comments`}
            reportId={reportId}
            label="Teacher Comments"
            placeholder="Teacher comments for this course…"
            labelClassName="text-[10px] font-bold uppercase tracking-widest text-muted-foreground block mb-2"
            textareaClassName="min-h-[80px] resize-none text-sm"
            buttonClassName="border-primary/20 text-primary hover:bg-primary/5"
          />
        </div>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

const FORM_CONFIG = {
  formSchema,
  defaultValues,
  fillPDF: (values, highlightRect) =>
    fillPDFProvincialGr912(values, highlightRect).catch((err) => { console.error('[pvrc9to12 fill]', err); throw err; }),
  templateUrl,
  STEPS,
  STEP_FIELDS,
  STEP_ANCHOR_FIELDS,
  SCHEMA_TO_PDF_FIELD,
  getPageIndex: (f) => PAGE_INDEX_FOR_SCHEMA_FIELD[f] ?? 0,
  getInitialPage: (s) => s <= 7 ? 1 : s <= 12 ? 2 : 3,
};

export const FreeTrialReportCardProvincialGr912202202Sem = ({ reportId, backUrl, contextInfo, defaultsGroupId, onRequestSave, readOnly = false } = {}) => {
  const hook = useReportForm(FORM_CONFIG, { reportId, defaultsGroupId, groupGrade: contextInfo?.grade });
  const watchedValues = hook.form.watch();
  const subSteps = COURSE_SUB_STEPS.map((s) => ({
    id: s.id,
    title: (watchedValues[`course${s.courseNum}Title`] || '').slice(0, 4).trim() || `C${s.courseNum}`,
  }));

  const renderStep = (form, currentStep) => {
    const { control } = form;

    if (currentStep === 1) {
      return (
        <div className="space-y-5">
          <SectionHeading title="Student Information" subtitle="Applies to all pages of the report" />
          <div className="rounded-2xl border border-border bg-card shadow-sm p-5 space-y-4">
            <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Report Header</p>
            <div className="grid grid-cols-3 gap-4">
              {[
                { name: 'semester',        label: 'Semester'         },
                { name: 'reportingPeriod', label: 'Reporting Period' },
                { name: 'date',            label: 'Date'             },
              ].map(({ name, label }) => (
                <FormField key={name} control={control} name={name} render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{label}</FormLabel>
                    <FormControl><Input {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              ))}
            </div>
          </div>
          <div className="rounded-2xl border border-border bg-card shadow-sm p-5 space-y-4">
            <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Student Details</p>
            <FormFieldGrid
              fields={[
                { name: 'studentName',        label: 'Student Name',        colSpan: 2 },
                { name: 'oen',                label: 'OEN'                              },
                { name: 'grade',              label: 'Grade'                            },
                { name: 'homeroom',           label: 'Homeroom'                         },
                { name: 'principal',          label: 'Principal'                        },
                { name: 'studentAddress',     label: 'Student Address',     colSpan: 2 },
                { name: 'schoolCouncilChair', label: 'School Council Chair', colSpan: 2 },
              ]}
              control={control}
              labelClassName="text-xs font-semibold text-muted-foreground uppercase tracking-wide"
            />
          </div>
        </div>
      );
    }

    if (currentStep === 2) {
      return (
        <div className="space-y-5">
          <SectionHeading title="School Information" />
          <div className="rounded-2xl border border-border bg-card shadow-sm p-5 space-y-4">
            <FormFieldGrid
              fields={[
                { name: 'schoolName',      label: 'School Name',    colSpan: 2 },
                { name: 'schoolTelephone', label: 'Telephone'                   },
                { name: 'schoolFax',       label: 'Fax'                         },
                { name: 'schoolAddress',   label: 'School Address', colSpan: 2 },
              ]}
              control={control}
              labelClassName="text-xs font-semibold text-muted-foreground uppercase tracking-wide"
            />
          </div>
        </div>
      );
    }

    if (currentStep === 3) {
      return (
        <div className="space-y-5">
          <SectionHeading title="Board Information" />
          <div className="rounded-2xl border border-border bg-card shadow-sm p-5 space-y-4">
            <FormFieldGrid
              fields={[
                { name: 'boardName',         label: 'Board Name',      colSpan: 2 },
                { name: 'boardEmailWebsite', label: 'Email / Website', colSpan: 2 },
                { name: 'boardAddress',      label: 'Board Address',   colSpan: 2 },
              ]}
              control={control}
              labelClassName="text-xs font-semibold text-muted-foreground uppercase tracking-wide"
            />
          </div>
        </div>
      );
    }

    if (currentStep >= 4 && currentStep <= 12) {
      const courseNum = currentStep - 3;
      const semLabel = courseNum <= 4 ? 'Semester 1' : 'Semester 2';
      return (
        <div className="space-y-4">
          <SectionHeading title={`Course ${courseNum}`} subtitle={semLabel} />
          <CourseCard key={currentStep} courseNum={courseNum} semLabel={semLabel} form={form} reportId={reportId} />
        </div>
      );
    }

    if (currentStep === 13) {
      return (
        <div className="space-y-5">
          <SectionHeading title="Credit Summary" subtitle="Total credits earned this report and to date" />
          <div className="rounded-2xl border border-border bg-card shadow-sm overflow-hidden">
            <div className="grid bg-muted/50 border-b border-border px-5 py-2.5" style={{ gridTemplateColumns: '1fr 80px 80px' }}>
              <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Subject / Category</span>
              <span className="text-[10px] font-bold uppercase tracking-widest text-primary text-center">This Report</span>
              <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground text-center">To Date</span>
            </div>
            {CREDIT_ROWS.map((row, i) => (
              <div key={row.thisReportField} className={`grid items-center px-5 py-2.5 ${i < CREDIT_ROWS.length - 1 ? 'border-b border-border' : ''} ${row.isTotal ? 'bg-primary/5 font-semibold' : ''}`} style={{ gridTemplateColumns: '1fr 80px 80px' }}>
                <span className={`text-sm ${row.isTotal ? 'font-bold text-primary' : 'text-foreground'}`}>{row.label}</span>
                <FormField control={control} name={row.thisReportField} render={({ field }) => (
                  <FormItem className="flex justify-center"><FormControl><input {...field} maxLength={4} inputMode="numeric" className="w-14 h-8 text-center text-sm font-semibold border border-border rounded-lg bg-input focus:border-primary focus:outline-none transition-colors" /></FormControl></FormItem>
                )} />
                <FormField control={control} name={row.toDateField} render={({ field }) => (
                  <FormItem className="flex justify-center"><FormControl><input {...field} maxLength={4} inputMode="numeric" className="w-14 h-8 text-center text-sm font-semibold border border-border rounded-lg bg-input focus:border-primary focus:outline-none transition-colors" /></FormControl></FormItem>
                )} />
              </div>
            ))}
          </div>
          <div className="rounded-2xl border border-border bg-card shadow-sm p-5 space-y-3">
            <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Specialist High Skills Major (SHSM)</p>
            <FormFieldGrid
              fields={[
                { name: 'specialistHighSkillsMajorName',             label: 'SHSM Name',         colSpan: 2 },
                { name: 'specialistHighSkillsMajorTotalRequired',    label: 'Total Required'                  },
                { name: 'specialistHighSkillsMajorEarnedThisReport', label: 'Earned This Report'              },
                { name: 'specialistHighSkillsMajorEarnedToDate',     label: 'Earned To Date'                  },
              ]}
              control={control}
              labelClassName="text-xs font-semibold text-muted-foreground uppercase tracking-wide"
            />
          </div>
          <div className="rounded-2xl border border-border bg-card shadow-sm p-5 space-y-4">
            <div className="flex items-center gap-4">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">OSSL Requirement</span>
              <FormField control={control} name="oSSLiteracyRequirementCompleted" render={({ field }) => (
                <button type="button" onClick={() => field.onChange(field.value === 'yes' ? '' : 'yes')}
                  className={`px-3 py-1.5 rounded-lg border-2 text-xs font-bold transition-colors cursor-pointer ${
                    field.value === 'yes' ? 'bg-primary border-primary text-white' : 'bg-input border-border text-muted-foreground hover:bg-accent'
                  }`}
                >Completed</button>
              )} />
            </div>
            <FormFieldGrid
              fields={[
                { name: 'schoolUse',              label: 'School Use',     colSpan: 2 },
                { name: 'principalSignatureDate', label: 'Signature Date', colSpan: 2 },
              ]}
              control={control}
              labelClassName="text-xs font-semibold text-muted-foreground uppercase tracking-wide"
            />
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <ReportFormShell
      hook={hook}
      title="Ontario Provincial Report Card - Gr. 9–12"
      reportId={reportId}
      backUrl={backUrl}
      contextInfo={contextInfo}
      defaultsGroupId={defaultsGroupId}
      onRequestSave={onRequestSave}
      STEPS={STEPS}
      PRESETS={PRESETS}
      defaultPageCount={3}
      StepIndicator={StepIndicator}
      stepIndicatorProps={{ steps: TOP_STEPS, subSteps }}
      renderStep={renderStep}
      formContentClassName="flex-1 overflow-y-auto px-5 py-5 bg-muted/50"
      readOnly={readOnly}
    />
  );
};
